import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { PaginationState } from "@/components/Datatable/components/DataTablePagination/types";
import { archiveDiagnosticAttempt } from "../api/archiveDiagnosticAttempt";
import { fetchCompanyDiagnostics } from "../api/fetchCompanyDiagnostics";
import { isCancellation } from "../utils/rejections";
import type { RecordLoadStatus } from "../utils/loadStatus";
import type { CompanyDiagnosticsResult } from "../types/diagnostic";

/**
 * One of the sizes `DataTablePagination` offers — [10, 20, 30, 40, 50] (D19).
 *
 * Not the endpoint's own default of 25: a size the items-per-page control has
 * no option for leaves that control blank until it is touched. The endpoint
 * default is left alone for its other clients, and the page size is sent
 * explicitly instead.
 */
export const COMPANY_HISTORY_PAGE_SIZE = 20;

/** A company id that names nothing. See `DiagnosticNotFound`. */
const isNotFound = (error: unknown): boolean =>
  axios.isAxiosError(error) && error.response?.status === 404;

/**
 * One page of one company's diagnostic history.
 *
 * Paging is server-side. Each attempt embeds a full instrument tree, so the
 * unpaged payload grew without bound with every diagnostic a company ever ran —
 * and the endpoint now caps a page at 25 by default, which the screen used to
 * mistake for the whole history.
 *
 * Archived attempts are filtered **server-side** (`includeArchived`, D27), so
 * the rows, `totalCount` and `hasMore` describe one set. Hiding them shrinks
 * that set, which is why the toggle settles the page as well as the filter.
 *
 * Archiving re-reads the page only when it has to. With archived rows on show
 * the server returns the whole history either way and orders it by
 * `createdAt DESC, id DESC`, so a row that flips `isArchived` cannot move —
 * the archive response is patched straight in, and re-reading the page to
 * learn one boolean flipped would cost every attempt's instrument tree to
 * change one badge. With archived rows hidden the flip takes the row *out* of
 * the set: it leaves the page, the total drops and later rows shift forward,
 * and only the server can say what the page holds now.
 */
export function useCompanyDiagnostics(companyId: number) {
  const [detail, setDetail] = useState<CompanyDiagnosticsResult | null>(null);
  /**
   * Which of the four things to draw — see `RecordLoadStatus`. A null `detail`
   * on its own cannot say whether the history is still coming, was never
   * there, or could not be read, and drawing the wrong one of those was this
   * screen's recurring defect in two successive review rounds.
   */
  const [status, setStatus] = useState<RecordLoadStatus>("loading");
  const [archivingId, setArchivingId] = useState<number | null>(null);
  const [showArchived, setShowArchivedState] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: COMPANY_HISTORY_PAGE_SIZE,
  });
  const abortControllerRef = useRef<AbortController | null>(null);

  const cancelPendingRequest = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // A different company is a different history; page 3 of the old one names
  // nothing in the new one. Returning `prev` unchanged when already on page 1
  // lets React bail out, so the common case costs no extra render or fetch.
  //
  // The payload goes with it. Held over, the previous company's history renders
  // under the new address for the length of the fetch — `loading && !detail` is
  // false while `detail` is the old one — so the screen named one company in
  // its title and listed another's diagnostics underneath. Keyed on
  // `companyId` alone: paging within a company must not blank the table.
  useEffect(() => {
    setDetail(null);
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, [companyId]);

  const requestData = useMemo(
    () => ({
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      includeArchived: showArchived,
    }),
    [pagination, showArchived],
  );

  // Hiding archived attempts shrinks the history, so the page in view can fall
  // off the end of it — page 3 of 30 names nothing in a set of 10. The clamp
  // below would eventually recover, but only after fetching and rendering an
  // empty page under a live pager, so the filter settles the page itself. Both
  // updates land in one render, so it costs one fetch, not two; and returning
  // `prev` unchanged when already on page 1 keeps the common case free.
  const setShowArchived = useCallback((next: boolean) => {
    setShowArchivedState(next);
    setPagination((prev) =>
      prev.pageIndex === 0 ? prev : { ...prev, pageIndex: 0 },
    );
  }, []);

  const refresh = useCallback(() => {
    // A junk URL segment reaches here as `NaN`. There is nothing to fetch, but
    // an early return still has to settle the status it owns: it starts
    // "loading", so returning bare left the screen on a skeleton for ever,
    // waiting on a request that was never made. `notFound` is the honest
    // terminal state — the address names nothing, and nothing failed. Any
    // request already in flight belonged to the *previous* company and must
    // not land under this one.
    if (!Number.isFinite(companyId)) {
      cancelPendingRequest();
      setStatus("notFound");
      return;
    }
    cancelPendingRequest();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    setStatus("loading");
    fetchCompanyDiagnostics(companyId, requestData, controller.signal)
      .then((loaded) => {
        setDetail(loaded);
        setStatus("loaded");
      })
      .catch((error) => {
        // A cancelled request does not own the status. It settles *after* the
        // request that superseded it has already set the flag, so settling it
        // unconditionally hid the spinner for a fetch still in flight — and
        // would now report a supersession as a failure.
        if (isCancellation(error)) return;
        // Not local error handling: a 404 here means the address names no
        // company, so there is nothing to load and nothing failed. Recording
        // that is a render state, exactly as in `AuditTrail`.
        //
        // Every other failure has already reached the user through the global
        // interceptor, and `detail` stays null so the screen shows nothing it
        // cannot vouch for — but it must still reach a *terminal* state, or
        // the page stands on its skeleton for ever waiting for a request that
        // has already failed. Rethrowing was never the answer: `.finally()`
        // propagates it and no caller awaits this.
        setStatus(isNotFound(error) ? "notFound" : "failed");
      });
  }, [cancelPendingRequest, companyId, requestData]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => cancelPendingRequest, [cancelPendingRequest]);

  // A page past the end of a real history. The endpoint answers it with the
  // company named and no rows — *not* with a null company — so it must not be
  // read as "never diagnosed"; the page index is simply stale, and the fix is
  // to land on the last page that exists and re-fetch.
  useEffect(() => {
    if (detail === null) return;
    const lastPageIndex = Math.max(
      0,
      Math.ceil(detail.totalCount / pagination.pageSize) - 1,
    );
    if (pagination.pageIndex > lastPageIndex) {
      setPagination((prev) => ({ ...prev, pageIndex: lastPageIndex }));
    }
  }, [detail, pagination]);

  const handlePageChange = useCallback(
    (pageIndex: number, pageSize: number) => {
      setPagination({ pageIndex, pageSize });
    },
    [],
  );

  const setArchived = useCallback(
    (attemptId: number, archived: boolean) => {
      setArchivingId(attemptId);
      archiveDiagnosticAttempt(attemptId, archived)
        .then((updated) => {
          // The row has left the set this page is drawn from: it drops out of
          // the rows, out of `totalCount` and out of `hasMore` together, and
          // rows from later pages move up to fill the gap. None of that can be
          // computed here — the row that fills the gap was never in the
          // payload — so the page is re-read. See the note on the hook.
          if (updated.isArchived && !showArchived) {
            refresh();
            return;
          }
          setDetail((prev) =>
            prev === null
              ? prev
              : {
                  ...prev,
                  attempts: prev.attempts.map((attempt) =>
                    attempt.id === updated.id
                      ? {
                          ...attempt,
                          archivedAt: updated.archivedAt,
                          isArchived: updated.isArchived,
                        }
                      : attempt,
                  ),
                },
          );
        })
        // The interceptor has already told the user and the row is left exactly
        // as it was; catching only stops the rejection surfacing as an unhandled
        // promise rejection, and adds no error state of its own (D10).
        .catch(() => undefined)
        .finally(() => setArchivingId(null));
    },
    [refresh, showArchived],
  );

  return {
    detail,
    status,
    loading: status === "loading",
    notFound: status === "notFound",
    archivingId,
    pagination,
    handlePageChange,
    refresh,
    setArchived,
    showArchived,
    setShowArchived,
  };
}
